import { useEffect, useState } from 'react';
import { payoutsApi, affiliatesApi } from '@/lib/api';
import { Payout } from '@/types';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import clsx from 'clsx';

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

const statusStyles: Record<string, string> = {
  pending_approval: 'badge-warning',
  pending: 'badge-warning',
  processing: 'badge-info',
  completed: 'badge-success',
  failed: 'badge-danger',
  rejected: 'badge-danger',
  reversed: 'badge-danger',
};

interface WithdrawalInfo {
  availableBalance: number;
  pendingBalance: number;
  totalEarnings: number;
  paidBalance: number;
  minimumWithdrawal: number;
  withdrawalProcessingFee: number;
  estimatedNetAmount: number;
  canWithdraw: boolean;
  paymentMethods: {
    stripeConnect: {
      configured: boolean;
      status: string;
      accountId?: string;
    };
    stripeBank: {
      configured: boolean;
      status: string;
      last4?: string;
      country?: string;
    };
  };
  preferredMethod: string | null;
  organizationPayoutsEnabled: boolean;
  contactMessage?: string;
  payableCommissionCount: number;
}

export default function AffiliatePayouts() {
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [withdrawalInfo, setWithdrawalInfo] = useState<WithdrawalInfo | null>(null);
  const [isConnectingStripe, setIsConnectingStripe] = useState(false);
  const [isSettingUpBank, setIsSettingUpBank] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [withdrawalAmount, setWithdrawalAmount] = useState<string>('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
  const [isRequestingWithdrawal, setIsRequestingWithdrawal] = useState(false);
  const [isSettingDefault, setIsSettingDefault] = useState(false);

  const fetchData = async () => {
    try {
      const [payoutsRes, withdrawalRes] = await Promise.all([
        payoutsApi.listMine(),
        affiliatesApi.getWithdrawalInfo(),
      ]);

      if (payoutsRes.data?.success) {
        setPayouts(payoutsRes.data?.data?.payouts ?? []);
      }
      if (withdrawalRes.data?.success) {
        const info = withdrawalRes.data?.data as WithdrawalInfo;
        setWithdrawalInfo(info);
        // Set default payment method if available
        if (info.preferredMethod) {
          setSelectedPaymentMethod(info.preferredMethod);
        } else if (info.paymentMethods.stripeConnect.status === 'active') {
          setSelectedPaymentMethod('stripe_connect');
        } else if (info.paymentMethods.stripeBank.status === 'active') {
          setSelectedPaymentMethod('stripe_bank');
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const initPage = async () => {
      // Check for Stripe redirect success/refresh
      const params = new URLSearchParams(window.location.search);

      if (params.get('stripe_bank_success') === 'true') {
        toast.success('Bank account connected successfully!');
        window.history.replaceState({}, '', window.location.pathname);
      }
      if (params.get('stripe_success') === 'true') {
        toast.success('Payment method connected successfully!');
        window.history.replaceState({}, '', window.location.pathname);
      }
      if (params.get('stripe_refresh') === 'true' || params.get('stripe_bank_refresh') === 'true') {
        toast('Please try connecting again', { icon: 'ℹ️' });
        window.history.replaceState({}, '', window.location.pathname);
      }

      // Always refresh bank status from Stripe to ensure database is up to date
      // This runs silently in the background before fetching withdrawal info
      try {
        await affiliatesApi.getStripeBankStatus();
      } catch {
        // Status check might fail if no bank is connected, that's OK
      }

      // Fetch all data (will have updated status from the call above)
      await fetchData();
    };

    initPage();
  }, []);

  const handleConnectStripe = async () => {
    setIsConnectingStripe(true);
    try {
      const response = await affiliatesApi.connectStripe();
      if (response.data.success) {
        window.location.href = response.data.data.url;
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || 'Failed to connect Stripe');
    } finally {
      setIsConnectingStripe(false);
    }
  };

  const handleSetupBank = async () => {
    setIsSettingUpBank(true);
    try {
      const response = await affiliatesApi.setupStripeBank();
      if (response.data.success) {
        window.location.href = response.data.data.url;
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || 'Failed to set up bank account');
    } finally {
      setIsSettingUpBank(false);
    }
  };

  const handleSetDefaultMethod = async (method: string) => {
    setIsSettingDefault(true);
    try {
      const response = await affiliatesApi.setDefaultPaymentMethod(method);
      if (response.data.success) {
        toast.success('Default payment method updated');
        fetchData();
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || 'Failed to set default method');
    } finally {
      setIsSettingDefault(false);
    }
  };

  const handleRequestWithdrawal = async () => {
    if (!selectedPaymentMethod) {
      toast.error('Please select a payment method');
      return;
    }

    setIsRequestingWithdrawal(true);
    try {
      const amount = withdrawalAmount ? Math.round(parseFloat(withdrawalAmount) * 100) : undefined;
      const response = await affiliatesApi.requestWithdrawal({
        amount,
        paymentMethod: selectedPaymentMethod,
      });
      if (response.data.success) {
        toast.success(response.data.data.message || 'Withdrawal request submitted');
        setShowWithdrawalModal(false);
        setWithdrawalAmount('');
        fetchData();
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || 'Failed to request withdrawal');
    } finally {
      setIsRequestingWithdrawal(false);
    }
  };

  const openWithdrawalModal = () => {
    if (withdrawalInfo) {
      setWithdrawalAmount((withdrawalInfo.availableBalance / 100).toFixed(2));
    }
    setShowWithdrawalModal(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rust"></div>
      </div>
    );
  }

  const safePayouts = payouts || [];
  const completedPayouts = safePayouts.filter(p => p.status === 'completed');
  const totalPaid = completedPayouts.reduce((sum, p) => sum + p.amount, 0);

  const hasActiveStripeConnect = withdrawalInfo?.paymentMethods.stripeConnect.status === 'active';
  const hasActiveBank = withdrawalInfo?.paymentMethods.stripeBank.status === 'active';
  const hasAnyPaymentMethod = hasActiveStripeConnect || hasActiveBank;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-display font-bold text-charcoal">Payouts</h2>
        <p className="text-charcoal/60 mt-1">Manage your withdrawals and payment methods</p>
      </div>

      {/* Contact Administrator Message (if org doesn't have Stripe) */}
      {withdrawalInfo && !withdrawalInfo.organizationPayoutsEnabled && (
        <div className="rounded-xl bg-tan/20 border border-tan p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-tan rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-charcoal" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="font-display font-semibold text-charcoal">Payment Not Available</h3>
              <p className="text-charcoal/70 mt-1">
                {withdrawalInfo.contactMessage || 'Contact Your Program Administrator for Payment'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Balance Overview */}
      {withdrawalInfo && withdrawalInfo.organizationPayoutsEnabled && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="rounded-xl bg-warm-white shadow-sm p-6 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-sage" />
              <div className="pl-2">
                <p className="text-sm text-charcoal/60 mb-1">Available for Withdrawal</p>
                <p className="font-serif text-3xl text-charcoal leading-none">
                  {formatCurrency(withdrawalInfo.availableBalance)}
                </p>
                <p className="text-xs text-charcoal/50 mt-1">
                  {withdrawalInfo.payableCommissionCount} commissions
                </p>
              </div>
            </div>
            <div className="rounded-xl bg-warm-white shadow-sm p-6 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-tan" />
              <div className="pl-2">
                <p className="text-sm text-charcoal/60 mb-1">Pending Approval</p>
                <p className="font-serif text-3xl text-charcoal leading-none">
                  {formatCurrency(withdrawalInfo.pendingBalance)}
                </p>
                <p className="text-xs text-charcoal/50 mt-1">Awaiting program approval</p>
              </div>
            </div>
            <div className="rounded-xl bg-warm-white shadow-sm p-6 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-rust" />
              <div className="pl-2">
                <p className="text-sm text-charcoal/60 mb-1">Total Paid</p>
                <p className="font-serif text-3xl text-charcoal leading-none">{formatCurrency(totalPaid)}</p>
                <p className="text-xs text-charcoal/50 mt-1">{completedPayouts.length} withdrawals</p>
              </div>
            </div>
            <div className="rounded-xl bg-warm-white shadow-sm p-6 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-charcoal" />
              <div className="pl-2">
                <p className="text-sm text-charcoal/60 mb-1">Lifetime Earnings</p>
                <p className="font-serif text-3xl text-charcoal leading-none">
                  {formatCurrency(withdrawalInfo.totalEarnings)}
                </p>
                <p className="text-xs text-charcoal/50 mt-1">All time</p>
              </div>
            </div>
          </div>

          {/* Withdrawal Button */}
          {hasAnyPaymentMethod && withdrawalInfo.canWithdraw && (
            <div className="rounded-xl bg-charcoal text-warm-white shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-display font-semibold">Ready to Withdraw</h3>
                  <p className="text-warm-white/70 mt-1">
                    You have {formatCurrency(withdrawalInfo.availableBalance)} available (minimum $50)
                  </p>
                </div>
                <button
                  onClick={openWithdrawalModal}
                  className="px-6 py-2.5 bg-rust text-warm-white rounded-md font-medium hover:bg-rust/90 transition-colors"
                >
                  Request Withdrawal
                </button>
              </div>
            </div>
          )}

          {/* Not enough balance message */}
          {hasAnyPaymentMethod && !withdrawalInfo.canWithdraw && withdrawalInfo.availableBalance < withdrawalInfo.minimumWithdrawal && (
            <div className="rounded-xl bg-cream border border-tan/30 p-6">
              <p className="text-charcoal/70">
                Minimum withdrawal is {formatCurrency(withdrawalInfo.minimumWithdrawal)}. You have{' '}
                {formatCurrency(withdrawalInfo.availableBalance)} available.
              </p>
            </div>
          )}
        </>
      )}

      {/* Payment Methods */}
      {withdrawalInfo?.organizationPayoutsEnabled && (
        <div className="rounded-xl bg-warm-white shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-tan/20">
            <h3 className="font-display text-lg font-bold text-charcoal">Payment Methods</h3>
          </div>
          <div className="p-6 space-y-4">
            {/* Stripe Connect */}
            <div className="flex items-center justify-between p-4 border border-tan/20 rounded-lg">
              <div className="flex items-center gap-4">
                <div className={clsx(
                  'w-10 h-10 rounded-full flex items-center justify-center',
                  hasActiveStripeConnect ? 'bg-sage' : 'bg-cream'
                )}>
                  <svg className={clsx('w-5 h-5', hasActiveStripeConnect ? 'text-white' : 'text-charcoal/40')} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    {hasActiveStripeConnect ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    )}
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-charcoal">Stripe Connect</p>
                  <p className="text-sm text-charcoal/60">
                    {hasActiveStripeConnect
                      ? 'Connected - Transfers take 1-3 business days'
                      : 'Receive payouts to your Stripe account'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {withdrawalInfo.preferredMethod === 'stripe_connect' && (
                  <span className="text-xs bg-sage/20 text-sage px-2 py-1 rounded">Default</span>
                )}
                {hasActiveStripeConnect ? (
                  withdrawalInfo.preferredMethod !== 'stripe_connect' && (
                    <button
                      onClick={() => handleSetDefaultMethod('stripe_connect')}
                      disabled={isSettingDefault}
                      className="text-sm text-rust hover:text-rust/80"
                    >
                      Set as Default
                    </button>
                  )
                ) : (
                  <button
                    onClick={handleConnectStripe}
                    disabled={isConnectingStripe}
                    className="px-4 py-2 bg-charcoal text-warm-white text-sm rounded-md hover:bg-charcoal/90"
                  >
                    {isConnectingStripe ? 'Connecting...' : 'Connect'}
                  </button>
                )}
              </div>
            </div>

            {/* Bank Account */}
            <div className="flex items-center justify-between p-4 border border-tan/20 rounded-lg">
              <div className="flex items-center gap-4">
                <div className={clsx(
                  'w-10 h-10 rounded-full flex items-center justify-center',
                  hasActiveBank ? 'bg-sage' : 'bg-cream'
                )}>
                  <svg className={clsx('w-5 h-5', hasActiveBank ? 'text-white' : 'text-charcoal/40')} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    {hasActiveBank ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    )}
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-charcoal">Bank Account</p>
                  <p className="text-sm text-charcoal/60">
                    {hasActiveBank
                      ? `****${withdrawalInfo.paymentMethods.stripeBank.last4 || '****'} - Transfers take 7-10 business days`
                      : 'Receive payouts directly to your bank'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {withdrawalInfo.preferredMethod === 'stripe_bank' && (
                  <span className="text-xs bg-sage/20 text-sage px-2 py-1 rounded">Default</span>
                )}
                {hasActiveBank ? (
                  withdrawalInfo.preferredMethod !== 'stripe_bank' && (
                    <button
                      onClick={() => handleSetDefaultMethod('stripe_bank')}
                      disabled={isSettingDefault}
                      className="text-sm text-rust hover:text-rust/80"
                    >
                      Set as Default
                    </button>
                  )
                ) : (
                  <button
                    onClick={handleSetupBank}
                    disabled={isSettingUpBank}
                    className="px-4 py-2 bg-charcoal text-warm-white text-sm rounded-md hover:bg-charcoal/90"
                  >
                    {isSettingUpBank ? 'Setting up...' : 'Set Up'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payouts Table */}
      <div className="rounded-xl bg-warm-white shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-tan/20">
          <h3 className="font-display text-lg font-bold text-charcoal">Withdrawal History</h3>
        </div>
        {safePayouts.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-cream rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-charcoal/30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
              </svg>
            </div>
            <p className="text-charcoal/60 font-medium">No withdrawals yet</p>
            <p className="text-sm text-charcoal/40 mt-2">
              When you request a withdrawal, it will appear here.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cream/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal/60 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal/60 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal/60 uppercase tracking-wider">Method</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal/60 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal/60 uppercase tracking-wider">Completed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-tan/10">
                {safePayouts.map((payout) => payout && (
                  <tr key={payout.payoutId} className="hover:bg-cream/30 transition-colors">
                    <td className="px-6 py-4 text-charcoal">{payout.createdAt ? format(new Date(payout.createdAt), 'MMM d, yyyy') : '—'}</td>
                    <td className="px-6 py-4 font-serif text-lg text-charcoal">
                      {formatCurrency(payout.amount ?? 0)}
                    </td>
                    <td className="px-6 py-4 text-charcoal/70">
                      {payout.method === 'stripe_connect' ? 'Stripe' : payout.method === 'stripe_bank' ? 'Bank' : payout.method}
                    </td>
                    <td className="px-6 py-4">
                      <span className={clsx('badge', statusStyles[payout.status] || 'badge-neutral')}>
                        {payout.status === 'pending_approval' ? 'Pending Review' : payout.status}
                      </span>
                      {payout.rejectionReason && (
                        <p className="text-xs text-red-600 mt-1">{payout.rejectionReason}</p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-charcoal/60">
                      {payout.completedAt
                        ? format(new Date(payout.completedAt), 'MMM d, yyyy')
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Withdrawal Modal */}
      {showWithdrawalModal && withdrawalInfo && (
        <div className="fixed inset-0 bg-charcoal/50 flex items-center justify-center z-50">
          <div className="bg-warm-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-tan/20 flex items-center justify-between">
              <h3 className="font-display text-lg font-bold text-charcoal">Request Withdrawal</h3>
              <button
                onClick={() => setShowWithdrawalModal(false)}
                className="text-charcoal/60 hover:text-charcoal"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-charcoal/60">Available Balance</p>
                <p className="font-serif text-2xl text-charcoal">{formatCurrency(withdrawalInfo.availableBalance)}</p>
                <p className="text-xs text-charcoal/50">Minimum withdrawal: {formatCurrency(withdrawalInfo.minimumWithdrawal)}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Amount</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/60">$</span>
                  <input
                    type="number"
                    value={withdrawalAmount}
                    onChange={(e) => setWithdrawalAmount(e.target.value)}
                    className="w-full pl-8 pr-4 py-2 border border-tan/30 rounded-md focus:ring-rust focus:border-rust"
                    placeholder="0.00"
                    step="0.01"
                    min={(withdrawalInfo.minimumWithdrawal / 100).toFixed(2)}
                    max={(withdrawalInfo.availableBalance / 100).toFixed(2)}
                  />
                </div>
                <button
                  onClick={() => setWithdrawalAmount((withdrawalInfo.availableBalance / 100).toFixed(2))}
                  className="text-xs text-rust hover:text-rust/80 mt-1"
                >
                  Withdraw Max
                </button>
              </div>

              {/* Fee breakdown */}
              {withdrawalAmount && parseFloat(withdrawalAmount) > 0 && (
                <div className="bg-cream/50 p-3 rounded-md space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-charcoal/70">Withdrawal Amount</span>
                    <span className="text-charcoal">{formatCurrency(parseFloat(withdrawalAmount) * 100)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-charcoal/70">Processing Fee</span>
                    <span className="text-charcoal">-{formatCurrency(withdrawalInfo.withdrawalProcessingFee)}</span>
                  </div>
                  <div className="border-t border-tan/30 pt-1 mt-1">
                    <div className="flex justify-between text-sm font-medium">
                      <span className="text-charcoal">You'll Receive</span>
                      <span className="text-sage">{formatCurrency(Math.max(0, parseFloat(withdrawalAmount) * 100 - withdrawalInfo.withdrawalProcessingFee))}</span>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Payment Method</label>
                <div className="space-y-2">
                  {hasActiveStripeConnect && (
                    <label className="flex items-center p-3 border border-tan/30 rounded-md cursor-pointer hover:bg-cream/30">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="stripe_connect"
                        checked={selectedPaymentMethod === 'stripe_connect'}
                        onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                        className="mr-3"
                      />
                      <div>
                        <p className="font-medium text-charcoal">Stripe Connect</p>
                        <p className="text-xs text-charcoal/60">1-3 business days</p>
                      </div>
                    </label>
                  )}
                  {hasActiveBank && (
                    <label className="flex items-center p-3 border border-tan/30 rounded-md cursor-pointer hover:bg-cream/30">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="stripe_bank"
                        checked={selectedPaymentMethod === 'stripe_bank'}
                        onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                        className="mr-3"
                      />
                      <div>
                        <p className="font-medium text-charcoal">Bank Account (****{withdrawalInfo.paymentMethods.stripeBank.last4 || '****'})</p>
                        <p className="text-xs text-charcoal/60">7-10 business days</p>
                      </div>
                    </label>
                  )}
                </div>
              </div>

              <div className="bg-tan/10 p-3 rounded-md">
                <p className="text-xs text-charcoal/70">
                  Your withdrawal request will be reviewed before processing. You'll be notified once approved.
                </p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-tan/20 flex justify-end gap-3">
              <button
                onClick={() => setShowWithdrawalModal(false)}
                className="px-4 py-2 text-charcoal/70 hover:text-charcoal"
              >
                Cancel
              </button>
              <button
                onClick={handleRequestWithdrawal}
                disabled={isRequestingWithdrawal || !selectedPaymentMethod}
                className="px-6 py-2 bg-rust text-warm-white rounded-md font-medium hover:bg-rust/90 disabled:opacity-50"
              >
                {isRequestingWithdrawal ? 'Submitting...' : 'Request Withdrawal'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
